import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { APPLICATIONS_REPORT } from '@/data/applicationsReport';
import type { ApplicationsReport, ApplicationsReportRow } from '@/types/reports';

interface UseApplicationsReportResult {
  data: readonly ApplicationsReportRow[];
  isLoading: boolean;
}

/**
 * Applications report data source — task 117.
 *
 * In mockup mode returns the fixture rows unchanged (server-sorted by
 * totalHours DESC at author time). In live mode calls the org-scoped
 * endpoint with the current `dateRange` and returns its `applications`
 * array. Mirrors the `useMonthlyReport` / `useDomains` shape.
 */
export function useApplicationsReport(): UseApplicationsReportResult {
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['applicationsReport', orgId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      const qsStr = qs.toString();
      const res = await apiFetch<ApplicationsReport>(
        `/api/v1/orgs/${orgId}/reports/applications${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
      return res.applications;
    },
    enabled: !mockupMode && !!orgId && !!dateRange.start && !!dateRange.end,
  });

  if (mockupMode) return { data: APPLICATIONS_REPORT, isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}
